import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import style from "./Flyout.module.css";
import { RemoveScroll } from "react-remove-scroll";

export type FlyoutAnchor = 'left' | 'right';
export type FlyoutPrefer = 'above' | 'below';

interface FlyoutProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  matchWidth?: boolean;
  anchor?: FlyoutAnchor;
  prefer?: FlyoutPrefer;
  isolateScrolling?: boolean;
  visible?: boolean;
}

const Flyout = forwardRef<HTMLDivElement, FlyoutProps>((props: FlyoutProps, forwardRef) => {
  const {
    matchWidth = true,
    anchor = 'left',
    prefer = 'below',
    isolateScrolling = true,
    children,
    className,
    visible: visibleProp = true,
    ...divProps
  } = props;
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>();
  const locatorRef = useRef<HTMLSpanElement>();

  const updateRefs = el => {
    ref.current = el;
    if (forwardRef) {
      if (forwardRef instanceof Function) {
        forwardRef(el);
      } else {
        forwardRef.current = el;
      }
    }
  };

  const update = () => {
    if (!ref.current || !locatorRef.current) {
      setVisible(false);
      return;
    }

    if (getComputedStyle(locatorRef.current.parentElement).position === 'static') {
      locatorRef.current.parentElement.style.position = 'relative';
    }

    // if this gets updated while visible, e.g. during a window resize, there's no way to know
    // its natural size except by unsetting the max height and waiting for a layout to occur
    const scrollOffset = ref.current.scrollTop;
    ref.current.style.maxHeight = 'unset';

    requestAnimationFrame(() => {
      if (!ref.current || !locatorRef.current) {
        setVisible(false);
        return;
      }

      const locatorRect = locatorRef.current.getBoundingClientRect();
      const flyoutRect = ref.current.getBoundingClientRect();
      const margin = 18;
      const maxBelow = window.innerHeight - locatorRect.bottom - margin;
      const maxAbove = locatorRect.top - margin;
  
      if (anchor === 'left') {
        ref.current.style.left = `${locatorRect.left}px`;
        ref.current.style.right = 'unset';
      } else if (anchor === 'right') {
        ref.current.style.right = `${window.innerWidth - locatorRect.right}px`;
        ref.current.style.left = 'unset';
      }
  
      if (matchWidth) {
        ref.current.style.width = `${locatorRect.width}px`;
      }
  
      const setBelow = () => {
        ref.current.style.top = `${locatorRect.bottom + 1}px`;
        ref.current.style.bottom = `unset`;
        ref.current.style.maxHeight = `${maxBelow}px`;
      };
      const setAbove = () => {
        ref.current.style.top = `unset`;
        ref.current.style.bottom = `${window.innerHeight - locatorRect.top + 1}px`;
        ref.current.style.maxHeight = `${maxAbove}px`;
      };
      const [setPref, maxPref, setAlt, maxAlt] = prefer === 'above'
        ? [setAbove, maxAbove, setBelow, maxBelow]
        : [setBelow, maxBelow, setAbove, maxAbove];
  
      if (flyoutRect.height > maxPref && maxAlt > maxPref) {
        setAlt();
      } else {
        setPref();
      }

      ref.current.scrollTop = scrollOffset;
  
      setVisible(true);
    });
  };

  useEffect(() => {
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(update, [ref.current, locatorRef.current, visibleProp]);

  const portal = createPortal(
    <div
      ref={updateRefs}
      className={`${className ?? ''} ${style.flyout} ${visible && visibleProp ? style.visible : ''}`}
      {...divProps}
    >
      {children}
    </div>,
    document.body,
  );
  return (
    <span ref={locatorRef} className={style.locator}>
      {isolateScrolling && visible && visibleProp ? <RemoveScroll>{portal}</RemoveScroll> : portal}
    </span>
  );
});

export default Flyout;
